import axios from "../../services/axiosService";

const URL = process.env.REACT_APP_API_URL;

export type OverlayBgStyle = "full" | "transparent" | "none";

export type ShortOverlay = {
  id?: string;
  text?: string;
  emoji?: string;
  startMs: number;
  endMs: number;
  x?: number;
  y?: number;
  bgStyle?: OverlayBgStyle;
  color?: string;
  fontFamily?: string;
  fontSize?: number;
};

export type ShortItem = {
  id: number;
  userId: number;
  videoUrl: string;
  thumbnailUrl?: string | null;
  caption?: string;
  durationSec?: number | null;
  width?: number | null;
  height?: number | null;
  hashtags?: string[];
  petType?: string | null;
  breed?: string | null;
  locationText?: string | null;
  overlays?: ShortOverlay[];
  muteOriginalAudio?: boolean;
  voiceoverUrl?: string | null;
  likeCount: number;
  commentCount: number;
  shareCount: number;
  saveCount: number;
  viewCount?: number;
  createdAt?: string;
  likedByMe?: boolean;
  savedByMe?: boolean;
  author: {
    userId: number;
    firstName?: string;
    lastName?: string;
    userName: string;
    profileImage?: string | null;
    followedByMe?: boolean;
  };
};

export const getShortsFeed = async (params?: {
  cursor?: number | null;
  limit?: number;
  hashtag?: string;
  sort?: "chrono" | "ranked";
  focusId?: number;
}) => {
  const res = await axios.get(`${URL}/api/v1/shorts/feed`, {
    params: {
      cursor: params?.cursor || undefined,
      limit: params?.limit || 10,
      hashtag: params?.hashtag || undefined,
      sort: params?.sort || "chrono",
      focusId: params?.focusId || undefined,
    },
  });
  return res.data;
};

export const getShortsByUser = async (userId: number, cursor?: number | null, limit = 24) => {
  const res = await axios.get(`${URL}/api/v1/shorts/user/${userId}`, {
    params: { cursor: cursor || undefined, limit },
  });
  return res.data;
};

export const getShortDetails = async (id: number) => {
  const res = await axios.get(`${URL}/api/v1/shorts/${id}`);
  return res.data;
};

export const createShort = async (payload: {
  videoUrl: string;
  thumbnailUrl?: string | null;
  caption?: string;
  durationSec: number;
  width?: number;
  height?: number;
  hashtags?: string[];
  petType?: string | null;
  breed?: string | null;
  locationText?: string | null;
  overlays?: ShortOverlay[];
  muteOriginalAudio?: boolean;
  voiceoverUrl?: string | null;
}) => {
  const res = await axios.post(`${URL}/api/v1/shorts`, payload);
  return res.data;
};

export const likeShort = async (id: number) => {
  const res = await axios.post(`${URL}/api/v1/shorts/${id}/like`);
  return res.data;
};

export const unlikeShort = async (id: number) => {
  const res = await axios.delete(`${URL}/api/v1/shorts/${id}/like`);
  return res.data;
};

export const saveShort = async (id: number) => {
  const res = await axios.post(`${URL}/api/v1/shorts/${id}/save`);
  return res.data;
};

export const unsaveShort = async (id: number) => {
  const res = await axios.delete(`${URL}/api/v1/shorts/${id}/save`);
  return res.data;
};

export const shareShort = async (id: number) => {
  const res = await axios.post(`${URL}/api/v1/shorts/${id}/share`);
  return res.data;
};

export const getShortLikes = async (id: number, offset = 0, limit = 30) => {
  const res = await axios.get(`${URL}/api/v1/shorts/${id}/likes`, { params: { offset, limit } });
  return res.data;
};

export const recordShortView = async (id: number) => {
  const res = await axios.post(`${URL}/api/v1/shorts/${id}/view`);
  return res.data;
};

export const getShortComments = async (id: number, offset = 0, limit = 30) => {
  const res = await axios.get(`${URL}/api/v1/shorts/${id}/comments`, { params: { offset, limit } });
  return res.data;
};

export const addShortComment = async (id: number, comment: string) => {
  const res = await axios.post(`${URL}/api/v1/shorts/${id}/comments`, { comment });
  return res.data;
};

export const editShortComment = async (id: number, commentId: number, comment: string) => {
  const res = await axios.patch(`${URL}/api/v1/shorts/${id}/comments/${commentId}`, { comment });
  return res.data;
};

export const deleteShortComment = async (id: number, commentId: number) => {
  const res = await axios.delete(`${URL}/api/v1/shorts/${id}/comments/${commentId}`);
  return res.data;
};

export const reportShort = async (id: number, reason: string) => {
  const res = await axios.post(`${URL}/api/v1/shorts/${id}/report`, { reason });
  return res.data;
};

export const deleteShort = async (id: number) => {
  const res = await axios.delete(`${URL}/api/v1/shorts/${id}`);
  return res.data;
};
